import { View, Text, StyleSheet, ScrollView, ActivityIndicator, FlatList, TouchableOpacity } from 'react-native'
import React, { useEffect, useState } from 'react'
import { useNavigation } from '@react-navigation/native';
import ScreenWrapper from '../../Components/ScreenWrapper'
import Header from '../../Components/Header'
import { hp, wp } from '../../helpers/common'
import { fetchNotifications } from '../../services/notificationService'
import { useAuth } from '../../Utils/AuthProvider'
import NotificationItem from '../../Components/NotificationItem'
import { theme } from '../../constants/theme'

// Define types
interface Notification {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  read: boolean;
  type: string;
  senderId?: string;
  receiverId?: string;
}

const Notifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(20);
  const { user } = useAuth();
  const navigation = useNavigation<any>();

  // Create a router-like object that uses navigation
  const router = {
    push: (params: any) => {
      if (params.pathname === 'postDetails') {
        (navigation as any).navigate('postDetails', params.params);
      }
    }
  };

  useEffect(() => {
    getNotifications();
  }, []);

  const getNotifications = async (isLoadMore = false) => {
    if (!user?.id) return;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const offset = isLoadMore ? currentPage * pageSize : 0;
      const limit = pageSize;

      let res = await fetchNotifications(user.id, limit, offset);
      if (res.success && res.data) {
        // Ensure we don't exceed the limit
        const safeData = res.data.slice(0, limit);
        
        if (isLoadMore) {
          // Check for duplicates before appending
          setNotifications(prev => {
            const existingIds = new Set(prev.map(n => n.id));
            const newNotifications = safeData.filter(n => !existingIds.has(n.id));
            return [...prev, ...newNotifications];
          });
          setCurrentPage(prev => prev + 1);
        } else {
          setNotifications(safeData);
          setCurrentPage(1);
        }
        
        // Check if we have more data - only if we got the full limit
        setHasMore(safeData.length >= limit);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      getNotifications(true);
    }
  };

  return (
    <ScreenWrapper bg="white">
      <View style={styles.container}>
        <Header title="Notifications" />

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
          </View>
        ) : (
          <FlatList
            data={notifications}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.listStyle}
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => (
              <NotificationItem 
                item={item} 
                router={router}
              />
            )}
            // REMOVED: onEndReached - causes automatic loading of all notifications
            // Replaced with "Load More" button for better performance
            ListEmptyComponent={
              <Text style={styles.noData}>No notifications yet</Text>
            }
            ListFooterComponent={
              hasMore ? (
                <View style={styles.loadMoreContainer}>
                  <TouchableOpacity 
                    onPress={handleLoadMore}
                    style={[styles.loadMoreButton, loadingMore && styles.loadMoreButtonDisabled]}
                    disabled={loadingMore}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.loadMoreText}>
                      {loadingMore ? 'Loading...' : 'Load More Notifications'}
                    </Text>
                  </TouchableOpacity>
                </View>
              ) : notifications.length > 0 ? (
                <Text style={styles.noMoreData}>No more notifications</Text>
              ) : null
            }
          />
        )}
      </View>
    </ScreenWrapper>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: wp(4),
  },
  listStyle: {
    paddingVertical: 20,
    gap: 10
  },
  noData: {
    fontSize: hp(1.8),
    fontWeight: "500",
    color: theme.colors.text,
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: hp(5),
  },
  loadingMoreContainer: {
    paddingVertical: hp(2),
    alignItems: 'center',
  },
  noMoreData: {
    fontSize: hp(1.6),
    color: theme.colors.text,
    textAlign: 'center',
    paddingVertical: hp(2),
  },
  loadMoreContainer: {
    marginVertical: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadMoreButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: wp(8),
    paddingVertical: hp(1.5),
    borderRadius: theme.radius.lg,
    minWidth: wp(40),
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadMoreText: {
    color: 'white',
    fontSize: hp(1.8),
    fontWeight: theme.fonts.semibold || '600',
  },
  loadMoreButtonDisabled: {
    opacity: 0.6,
  },
})

export default Notifications